import React from "react";
import {
  Plane,
  ShieldCheck,
  Mail,
  CheckCircle2,
  Clock,
  CreditCard,
  BadgeCheck,
  Wallet,
} from "lucide-react";

function formatDuration(iso) {
  if (!iso) return "";
  const h = (iso.match(/(\d+)H/) || [])[1];
  const m = (iso.match(/(\d+)M/) || [])[1];
  const parts = [];
  if (h) parts.push(`${h} h`);
  if (m) parts.push(`${m} min`);
  return parts.join(" ") || iso;
}

function formatDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;

  const date = d.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  const time = d.toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  });

  return `${date} • ${time}`;
}

function formatPrice(amount, currency = "XOF") {
  const n = Number(amount);
  if (Number.isNaN(n) || n === 0) return "—";
  return `${new Intl.NumberFormat("fr-FR").format(Math.round(n))} ${currency}`;
}

const TYPE_LABELS = { ADULT: "Adulte", CHILD: "Enfant", HELD_INFANT: "Bébé" };

function normalizeFlights(raw) {
  if (Array.isArray(raw)) return raw.filter(Boolean);
  if (!raw) return [];

  if (Array.isArray(raw.flightOffers)) return raw.flightOffers.filter(Boolean);
  if (Array.isArray(raw.flights)) return raw.flights.filter(Boolean);
  if (Array.isArray(raw.offers)) return raw.offers.filter(Boolean);
  if (Array.isArray(raw.itineraries)) return [{ itineraries: raw.itineraries }];
  if (raw.itineraries && Array.isArray(raw.itineraries)) return [raw];

  if (raw.data && Array.isArray(raw.data.flightOffers)) return raw.data.flightOffers.filter(Boolean);
  if (raw.data && Array.isArray(raw.data.flights)) return raw.data.flights.filter(Boolean);
  if (raw.data && Array.isArray(raw.data.offers)) return raw.data.offers.filter(Boolean);

  return raw?.flightOffers || raw?.flights || raw?.offers || raw?.itineraries ? [raw] : [];
}

function normalizePassengers(raw) {
  if (Array.isArray(raw)) return raw.filter(Boolean);
  if (!raw) return [];

  if (Array.isArray(raw.travelers)) return raw.travelers.filter(Boolean);
  if (Array.isArray(raw.passengers)) return raw.passengers.filter(Boolean);
  if (Array.isArray(raw.items)) return raw.items.filter(Boolean);

  if (raw.data && Array.isArray(raw.data.travelers)) return raw.data.travelers.filter(Boolean);
  if (raw.data && Array.isArray(raw.data.passengers)) return raw.data.passengers.filter(Boolean);

  return raw?.travelers || raw?.passengers || raw?.items ? [raw] : [];
}

const ReservationReceipt = ({ data }) => {
  if (!data) {
    return (
      <div className="p-6 text-center text-gray-500">
        Aucune donnée de réservation disponible.
      </div>
    );
  }

  const flights = normalizeFlights(data.flight_data || data.flightData || data.flight || data.offers || data.flightOffers);
  const passengers = normalizePassengers(data.passenger_data || data.passengerData || data.passengers || data.travelers);
  const payment = data.payment || {};
  const paymentStatus = payment.status || (data.status === "success" ? "success" : "pending");
  const payed = paymentStatus === "success";
  const reservationStatus = data.reservation_status || data.booking?.status || (payed ? "confirmée" : "en_attente");
  const travelStatusLabel =
    reservationStatus === "confirmée"
      ? "Réservation bouclée"
      : reservationStatus === "en_attente"
      ? "Réservation en attente / validation manuelle"
      : reservationStatus === "en_cours"
      ? "Réservation en cours"
      : reservationStatus === "failed"
      ? "Réservation refusée"
      : "Statut de réservation non défini";
  const amountPayé =
    payment.amount != null
      ? payment.amount
      : flights[0]?.adjustedPrice ?? flights[0]?.price?.total ?? null;
  const currency = payment.currency || "XOF";
  const reference = data.reference || payment.reference || data.booking?.bookingId || "";
  const provider = payment.provider || "paystack";
  const customerEmail = data.email || data.customer_email || passengers[0]?.contact?.emailAddress || "—";
  const paymentDate =
    payment.paid_at ||
    payment.created_at ||
    data.created_at ||
    data.payment_date ||
    new Date().toISOString();

  const totalPassengers = passengers.length || 1;
  const summaryItems = [
    { label: "Montant payé", value: formatPrice(amountPayé, currency), icon: Wallet },
    { label: "Référence de paiement", value: reference || "—", icon: CreditCard },
    { label: "Date de paiement", value: formatDateTime(paymentDate), icon: BadgeCheck },
  ];
  const itinerarySummary = flights.flatMap((flight) =>
    (flight.itineraries || []).map((itinerary) => {
      const segments = itinerary.segments || [];
      if (!segments.length) return null;
      const first = segments[0];
      const last = segments[segments.length - 1];
      return {
        route: `${first.departure?.iataCode || "?"} → ${last.arrival?.iataCode || "?"}`,
        dates: `${formatDateTime(first.departure?.at)} → ${formatDateTime(last.arrival?.at)}`,
        duration: formatDuration(itinerary.duration),
        flightNo: `${first.carrierCode || ""}${first.number ? ` ${first.number}` : ""}`,
      };
    })
  ).filter(Boolean);

  return (
    <div className="max-w-5xl mx-auto px-2 py-4 sm:px-4 print:px-0 print:py-0">
      <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_18px_45px_rgba(15,23,42,0.08)] print:rounded-none print:border-0 print:shadow-none">
        <div
          className={`relative px-5 py-6 sm:px-7 ${
            payed
              ? "bg-gradient-to-r from-emerald-600 via-emerald-500 to-green-500"
              : "bg-gradient-to-r from-amber-500 via-orange-500 to-amber-400"
          }`}
        >
          <div className="mb-6 flex items-center justify-between border-b border-white/25 pb-4 text-[10px] font-bold uppercase tracking-[0.28em] text-white/80">
            <span>Djater Voyage</span>
            <span>Document officiel</span>
          </div>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                {payed ? <CheckCircle2 className="h-8 w-8 text-white" /> : <Clock className="h-8 w-8 text-white" />}
              </div>
              <div>
                <p className="text-2xl font-black text-white">Reçu de paiement</p>
                <p className="text-sm text-white/85">
                  {payed ? "Paiement confirmé" : "Paiement en attente de validation"}
                </p>
              </div>
            </div>
            <div className="rounded-2xl bg-white/10 px-4 py-3 text-left sm:text-right backdrop-blur-sm">
              <p className="text-xs uppercase tracking-[0.2em] text-white/80">Total versé</p>
              <p className="mt-1 text-3xl font-black text-white">{formatPrice(amountPayé, currency)}</p>
              <p className="text-xs text-white/80">{provider.toUpperCase()} • {payed ? "Validé" : "En attente"}</p>
            </div>
          </div>
        </div>

          <div className="p-5 sm:p-7">
          <div className="mb-6 flex flex-col gap-3 border-b border-dashed border-slate-300 pb-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Réservation</p>
              <p className="mt-1 break-all font-mono text-lg font-black text-slate-800">{reference || "Référence en attente"}</p>
            </div>
            <div className={`w-fit rounded-full border px-3 py-1.5 text-xs font-bold ${
              reservationStatus === "confirmée"
                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                : reservationStatus === "en_attente"
                ? "border-amber-200 bg-amber-50 text-amber-700"
                : "border-slate-200 bg-slate-50 text-slate-600"
            }`}>
              {payed ? "Paiement validé" : "Paiement à vérifier"}
            </div>
          </div>
          <div className="mb-6 grid gap-4 md:grid-cols-3">
            {summaryItems.map(({ label, value, icon: Icon }) => (
              <div key={label} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                  <Icon className="h-4 w-4 text-blue-600" />
                  {label}
                </div>
                <p className="break-all text-base font-bold text-slate-800">{value}</p>
              </div>
            ))}
          </div>

          <div className="mb-6 rounded-2xl border border-blue-100 bg-blue-50 p-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-700">Référence de réservation</p>
                <p className="mt-2 font-mono text-lg font-bold text-slate-800">{reference || "—"}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-700">Contact</p>
                <p className="mt-2 flex items-center gap-2 text-base font-semibold text-slate-800 break-all">
                  <Mail className="h-4 w-4 shrink-0 text-blue-600" />
                  {customerEmail}
                </p>
              </div>
            </div>
          </div>

          <div className="mb-6 rounded-2xl border border-slate-200 bg-slate-50 p-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Statut de réservation</p>
                <p className={`mt-2 text-lg font-bold ${reservationStatus === "confirmée" ? "text-emerald-700" : reservationStatus === "en_attente" ? "text-amber-700" : "text-slate-700"}`}>
                  {travelStatusLabel}
                </p>
              </div>
              <div className="rounded-full bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm border border-slate-200">
                {payed ? "Paiement validé" : "Paiement à vérifier"}
              </div>
            </div>
          </div>

          {itinerarySummary.length > 0 && (
            <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="mb-4 text-lg font-bold text-slate-800">Résumé du voyage</h3>
              <div className="grid gap-3 md:grid-cols-2">
                {itinerarySummary.map((item, index) => (
                  <div key={`${item.route}-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-sm font-bold text-slate-800">{item.route}</p>
                    <p className="mt-2 text-xs text-slate-500">{item.dates}</p>
                    <div className="mt-3 flex items-center justify-between gap-3 text-xs text-slate-600">
                      <span>{item.duration}</span>
                      <span className="rounded-full bg-white px-2 py-1 font-medium">{item.flightNo || "Vol"}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {flights.length === 0 ? (
            <div className="mb-6 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-slate-500">
              Aucune donnée de vol disponible.
            </div>
          ) : (
            <div className="space-y-6 mb-6">
              {flights.map((flight, idx) => {
                const itineraries = flight.itineraries || [];
                const airlineCode =
                  flight.validatingAirlineCodes?.[0] || itineraries[0]?.segments?.[0]?.carrierCode || "";
                const firstLeg = itineraries[0]?.segments?.[0];
                const lastLeg = itineraries.at(-1)?.segments?.at(-1);

                return (
                  <div key={idx} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="mb-4 flex items-center gap-3 border-b border-slate-100 pb-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50">
                        <Plane className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-slate-800">
                          {firstLeg?.departure?.iataCode || "???"} → {lastLeg?.arrival?.iataCode || "???"}
                        </h3>
                        <p className="text-xs text-slate-500">
                          {itineraries.length === 2
                            ? "Aller / Retour"
                            : itineraries.length > 1
                            ? `${itineraries.length} trajets`
                            : "Aller simple"}
                          {airlineCode ? ` • ${airlineCode}` : ""}
                        </p>
                      </div>
                    </div>

                    {itineraries.map((it, i) => {
                      if (!it.segments || it.segments.length === 0) return null;
                      const first = it.segments[0];
                      const last = it.segments[it.segments.length - 1];

                      return (
                        <div key={i} className="mb-4 last:mb-0">
                          {itineraries.length > 1 && (
                            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">
                              {i === 0 ? "Aller" : "Retour"}
                            </p>
                          )}
                          <div className="flex flex-col gap-3 rounded-2xl bg-slate-50 p-4 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                              <p className="text-xl font-black text-slate-800">{first.departure?.iataCode || "?"}</p>
                              <p className="text-xs text-slate-500">{formatDateTime(first.departure?.at)}</p>
                            </div>
                            <div className="flex-1 text-center">
                              <p className="text-xs text-slate-500">{formatDuration(it.duration)}</p>
                              <div className="mt-2 flex items-center gap-2">
                                <span className="h-px flex-1 bg-slate-300" />
                                <Plane className="h-4 w-4 text-blue-500" />
                                <span className="h-px flex-1 bg-slate-300" />
                              </div>
                              <p className="mt-2 text-xs text-slate-500">
                                {first.carrierCode}
                                {first.number ? ` • Vol ${first.carrierCode}${first.number}` : ""}
                              </p>
                            </div>
                            <div className="text-left sm:text-right">
                              <p className="text-xl font-black text-slate-800">{last.arrival?.iataCode || "?"}</p>
                              <p className="text-xs text-slate-500">{formatDateTime(last.arrival?.at)}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}

          {passengers.length > 0 && (
            <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="mb-4 flex items-center gap-2 text-lg font-bold text-slate-800">
                <ShieldCheck className="h-5 w-5 text-blue-600" />
                Passagers ({totalPassengers})
              </h3>
              <div className="grid gap-3 md:grid-cols-2">
                {passengers.map((p, i) => (
                  <div key={i} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-sm font-black text-blue-700">
                      {(p.name?.firstName || "?").charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800">
                        {[p.name?.firstName, p.name?.lastName].filter(Boolean).join(" ") || "—"}
                      </p>
                      <p className="text-xs text-slate-500">
                        {TYPE_LABELS[p.travelerType] || p.travelerType || "Passager"}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-600" />
              <p className="text-sm text-emerald-800">
                {data.email_sent ? "Un email de confirmation a été envoyé" : "Un email de confirmation vous sera envoyé"} à votre adresse.
                Merci de garder ce reçu pour votre enregistrement et votre remboursement éventuel.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReservationReceipt;